import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import MessageInput from 'supportAssistant/components/Chat/MessageInput'
import { isChip } from 'supportAssistant/utils/actionsUtils'
import Action from 'supportAssistant/components/Chat/MessageMeta/Action'
import BodyText from 'LuxKit/Typography/BodyText'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { getLuxPlusSupportContactNumber } from 'selectors/supportContactSelector'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'
import { OptimizelyExperiments } from 'constants/optimizely'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { trackClientEvent } from 'analytics/analytics'

const CenterContainer = styled(Group)`
  flex: 1;
  padding: ${rem(16)};
`

const InputContainer = styled.div`
  width: 100%;
  max-width: ${rem(720)};
`

const ActionsContainer = styled.div`
  width: 100%;
  max-width: ${rem(720)};
`

interface Props {
  isTyping: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  className?: string
}

function ChatEmptyState(props: Props) {
  const { isTyping, actions, payload, className } = props
  const intl = useIntl()

  const chipActions = useMemo(() => actions?.filter(isChip) ?? [], [actions])

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusSupportContactNumber = useAppSelector((state) => getLuxPlusSupportContactNumber(state).numberHumanReadable)

  // Société Infinite takes precedence: show the member's concierge number regardless of LuxPlus.
  const isInfiniteMember = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const concierge = useInfiniteConciergeDetails()

  const { isAnyVariant: isEmptyChatFreeTextHidden } = useExperiment(OptimizelyExperiments.opexLESAEmptyChatFreeText)

  const hasTrackedCohort = useRef(false)
  useEffect(() => {
    if (!hasTrackedCohort.current) {
      hasTrackedCohort.current = true
      trackClientEvent({
        subject: 'support_assistant',
        action: isEmptyChatFreeTextHidden
          ? 'lesa-empty-chat-no-free-text'
          : 'lesa-empty-chat-with-free-text',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [isEmptyChatFreeTextHidden])

  const handlePillClick = useCallback(() => {
    trackClientEvent({
      subject: 'support_assistant',
      action: 'lesa-empty-chat-pill-clicked',
      category: 'logging',
      type: 'interaction',
      optimizelyEventId: '6310489135775744',
      optimizelyEventKey: 'lesa_empty_chat_pill_clicked',
    })
  }, [])

  return (
    <CenterContainer className={className} direction="vertical" horizontalAlign="center" verticalAlign="center" gap={16} fullWidth>
      <HeadingTextBlock variant="heading4" align="center">
        {intl.formatMessage({
          id: 'SEWHldBBDxE8nRNbCzf0',
          defaultMessage: 'How can we help you?',
          description: 'Heading shown on empty support chat state in the support area',
        })}
      </HeadingTextBlock>
      {isInfiniteMember && concierge ? (
        <BodyText variant="medium" align="center" colour="light-foreground-alt">
          <FormattedMessage
            id="uQazy7CAfSv7Eep0vNra"
            defaultMessage="You can skip the queue by calling your Infinite Concierge representative on {phoneNumber}"
            description="Message for Société Infinite members showing their concierge representative phone number"
            values={{ phoneNumber: concierge.phoneNumber }}
          />
        </BodyText>
      ) : canViewLuxPlusBenefits && luxPlusSupportContactNumber ? (
        <BodyText variant="medium" align="center" colour="light-foreground-alt">
          <FormattedMessage
            id="rdLJQ7vnaHehISaoqOun"
            defaultMessage="You can skip the queue by calling LuxPlus+ Priority Support on {phoneNumber}"
            description="Message for LuxPlus members showing priority support phone number"
            values={{ phoneNumber: luxPlusSupportContactNumber }}
          />
        </BodyText>
      ) : null}
      {!isEmptyChatFreeTextHidden && (
        <InputContainer>
          <MessageInput isTyping={isTyping} showDisclaimer={false} />
        </InputContainer>
      )}
      {chipActions.length > 0 && (
        <ActionsContainer>
          <Group
            direction="horizontal"
            tabletDirection="horizontal"
            wrap="wrap"
            horizontalAlign="center"
            verticalAlign="center"
            gap={8}
            fullWidth
          >
            {chipActions.map((action, index) => (
              <div key={`${action.text}-${index}`} onClick={handlePillClick}>
                <Action action={action} payload={payload} />
              </div>
            ))}
          </Group>
        </ActionsContainer>
      )}
    </CenterContainer>
  )
}

export default ChatEmptyState
